<script lang="ts" setup>
import type { Group, GroupMember } from '~/models/Group'
import type { FavoriteList } from '~/models/Favorites.js'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { StrictTaskUser, Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref, shallowRef, watch } from 'vue'
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  CheckboxGroupRoot,
  RadioGroupRoot
} from 'reka-ui'
import { storeToRefs } from 'pinia'
import { DateTime, Duration, Interval } from 'luxon'
import { createReusableTemplate } from '@vueuse/core'
import { faArrowTurnRight, faChevronDown, faChevronUp, faPlus, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { mapFilter, notNullOrUndefined } from '~/utils/arrayUtils'
import { AuToast } from '~/stores/toasts'
import useProductStore from '~/stores/product'
import useGroupsStore from '~/stores/groups'
import { useAuthStore } from '~/stores/auth'
import { UserRole } from '~/models/User/UserRole'
import { PendoFeature } from '~/models/Pendo'
import { GroupRole, GroupSource, GroupType } from '~/models/Group'
import { LocationType, TaskStatus } from '~/models/AssignTask'
import useSchoolYear from '~/composables/useSchoolYear'
import { useContentHelper } from '~/composables/useContentHelper'
import { useAssignTask } from '~/composables/useAssignTask'
import { SHARE_TAG } from '~/composables/useAssignTags'
import Select from '~/components/ui/Select.vue'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuRadio from '~/components/ui/AuRadio.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import SearchMembers from '~/components/settings/SettingsGroups/SearchMembers.vue'

const props = withDefaults(defineProps<{
  resource?: BaseItem
  list?: FavoriteList
  task?: Task
  showLabel?: boolean
}>(), {
  showLabel: false,
})

const emit = defineEmits<{
  taskUpdated: [task: Task]
}>()

const initialDateTime = DateTime.now().startOf('week').set({ hour: 7 })
const { t } = useI18n()
const { getProductFromResource } = useProductStore()
const { findGroupMembers, loadGroupMembers } = useGroupsStore()
const { sortedGroups } = storeToRefs(useGroupsStore())
const { shareSimple, updateTask } = useAssignTask()
const { isPackage } = useContentHelper()
const { hasOrganization, isTeacher, user, userId } = storeToRefs(useAuthStore())
const { schoolYearByWeek } = useSchoolYear()
const { createGroup } = useGroupsStore()

const dialogOpen = ref(false)
const isLoadingGroups = ref(false)
const selectedGroup = ref<Group>()
const textareaValue = ref('')

const currentWeek = shallowRef(DateTime.local().startOf('week'))
const selectedDateTime = shallowRef<DateTime>(initialDateTime.set({ weekday: DateTime.now().weekday }).plus({ day: 1 }))
const homework = ref(props.task?.homework ?? false)

const isEditMode = computed(() => !!props.task)
const shareWhen = ref<'now' | 'later'>(isEditMode.value ? 'later' : 'now')
const showShareButton = computed(() => isTeacher.value && hasOrganization.value)
const product = computed(() => props.resource ? getProductFromResource(props.resource) : null)
const dialogTitle = computed(() => isEditMode.value ? t('share.edit') : t('share.title'))

const step = ref(0)

function mapGroupMemberToTaskUser(user: GroupMember): StrictTaskUser {
  const existingUser = props.task?.users?.find(({ userId }) => userId === user.userId)
  return {
    userId: user.userId,
    firstName: user.firstName,
    lastName: user.lastName,
    fullName: user.fullName,
    status: existingUser?.status ?? TaskStatus.Open,
    primaryRole: user.role,
  }
}

const groupMap = computed(() =>
  new Map(
    sortedGroups
      .value
      .map(group => [
        group.groupId,
        findGroupMembers(group).map(user => mapGroupMemberToTaskUser(user))
      ])
  )
)

const userCache = new Map<string, StrictTaskUser>([])

const selectedGroupIds = ref<string[]>([])
const selectedUserIds = ref<string[]>([])

const selectedGroups = computed<Group[]>(
  () => mapFilter(
    selectedGroupIds.value,
    groupId => sortedGroups.value.find(group => group.groupId === groupId),
    notNullOrUndefined,
  )
)

const selectedUsers = computed<StrictTaskUser[]>({
  get: () => mapFilter(
    selectedUserIds.value,
    (userId) => {
      if (userCache.has(userId))
        return userCache.get(userId)

      for (const [_, members] of groupMap.value) {
        for (const user of members) {
          if (user.userId === userId)
            return user
        }
      }
    },
    notNullOrUndefined,
  ),

  set: (value: StrictTaskUser[]) => {
    value.forEach(user => userCache.set(user.userId, user))
    selectedUserIds.value = value.map(user => user.userId)
  },
})

const allSelectedUserIds = computed(() => [
  ...new Set(
    selectedUserIds
      .value
      .concat(...mapFilter(selectedGroupIds.value, id => groupMap.value.get(id)?.map(user => user.userId), notNullOrUndefined))
  )
])

const allSelectedUsers = computed(() => mapFilter(
  allSelectedUserIds.value,
  (userId) => {
    if (userCache.has(userId))
      return userCache.get(userId)

    for (const [_, members] of groupMap.value) {
      for (const user of members) {
        if (user.userId === userId)
          return user
      }
    }
  },
  notNullOrUndefined,
))

const isSubmitting = computed(() => isEditMode.value
  ? updateTask.isPending.value
  : shareSimple.isPending.value
)

const locationType = computed(() => {
  if (!props.resource)
    return LocationType.ParentLocation

  return isPackage(props.resource) ? LocationType.ParentLocation : LocationType.Location
})

const isTeacherItem = computed(() => props.resource?.access === 'teacher')

const isInFuture = computed(() => {
  if (props.task?.startDate)
    return selectedDateTime.value >= props.task.startDate

  return shareWhen.value === 'now'
    ? true
    : selectedDateTime.value > DateTime.now()
})

const canSubmit = computed(() =>
  isInFuture.value
  && !isLoadingGroups.value
  && (allSelectedUserIds.value.length > 0)
  && !!selectedDateTime.value
)

const initializeForm = () => {
  if (props.task) {
    const taskGroup = sortedGroups.value.find(group => props.task?.groups?.includes(group.groupId))
    if (taskGroup) {
      selectedGroup.value = taskGroup
    }

    textareaValue.value = props.task.message || ''
    selectedGroupIds.value = props.task.groups ?? []
    selectedUserIds.value = props.task.users
      ?.map(({ userId }) => userId)
      .filter((userId): userId is string => userId !== null && !selectedGroupIds.value.some(groupId => groupMap.value.get(groupId)?.some(user => user.userId === userId)))
      ?? []

    props.task.users?.forEach((user) => {
      if (user.userId && user.status && user.primaryRole) {
        userCache.set(user.userId, {
          userId: user.userId,
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          fullName: user?.fullName ?? '',
          status: user.status,
          primaryRole: user.primaryRole
        })
      }
    })

    const weekNr = props.task?.week || currentWeek.value.weekNumber
    selectedDateTime.value = props.task?.startDate
      ? props.task?.startDate.toLocal()
      : DateTime.fromObject({ weekNumber: weekNr, weekYear: schoolYearByWeek(weekNr) })
  }
}

async function submit() {
  if (user.value && !allSelectedUserIds.value.includes(user.value?.userId)) {
    selectedUserIds.value.push(user.value.userId)
  }

  const task: Partial<Task> = {
    name: props.resource?.title || props.list?.name || '',
    grades: props.resource?.grades,
    status: TaskStatus.Open,
    locationIds: props.resource ? [props.resource.locationId] : [],
    locationType: locationType.value,
    pathString: product.value?.aunivers.pathString ?? '/',
    subjects: product.value?.subjects || props.list?.subjects || selectedGroup.value?.subjects || [],
    tags: [SHARE_TAG],
    creator: user.value
      ? {
          firstName: user.value?.firstName,
          fullName: user.value?.fullName,
          lastName: user.value?.lastName
        }
      : undefined,
    ...(props.task ?? {}),
    groups: selectedGroupIds.value,
    users: allSelectedUsers.value,
    message: textareaValue.value,
    week: selectedDateTime.value.weekNumber,
    startDate: shareWhen.value === 'now'
      ? initialDateTime.toUTC()
      : selectedDateTime.value.toUTC(),
    homework: homework.value,
    favoriteList: props.task?.favoriteList ?? (props.list?.id ? { id: props.list.id } : undefined),
  }

  if (isEditMode.value) {
    await updateTask.mutateAsync(task as Task)
    emit('taskUpdated', task as Task)
  } else {
    await shareSimple.mutateAsync({
      newTask: task,
      users: { users: task.users || [] },
    })
  }
  dialogOpen.value = false
}

watch(user, () => {
  if (!user.value || !userId.value)
    return
  userCache.set(userId.value, { ...user.value, status: TaskStatus.Open, primaryRole: user.value.role[0] })
}, { immediate: true })
watch(dialogOpen, () => initializeForm())
watch(selectedGroups, (groups) => {
  groups.forEach(group => loadGroupMembers(group))
}, { immediate: true })

function unselectUser(userId: string) {
  const index = selectedUserIds.value.findIndex(id => id === userId)
  if (index >= 0) {
    selectedUserIds.value.splice(index, 1)
  }
}

function unselectGroup(groupId: string) {
  const index = selectedGroupIds.value.findIndex(id => id === groupId)
  if (index >= 0) {
    selectedGroupIds.value.splice(index, 1)
  }
}

const weeks = computed(() =>
  Interval
    .after(selectedDateTime.value.set({ weekYear: initialDateTime.weekYear, weekNumber: initialDateTime.weekNumber }), { weeks: 10 })
    .splitBy(Duration.fromDurationLike({ weeks: 1 }))
    .map(interval => interval.start)
    .filter((dt): dt is DateTime => dt !== null)
)
const days = computed(() =>
  Interval
    .after(selectedDateTime.value.set({ weekday: 1 }), { weeks: 1 })
    .splitBy(Duration.fromDurationLike({ days: 1 }))
    .map(interval => interval.start)
    .filter((dt): dt is DateTime => dt !== null)
)
const times = computed(() =>
  Interval
    .after(selectedDateTime.value.set({ hour: 0, minute: 0 }), { days: 1 })
    .splitBy(Duration.fromDurationLike({ hours: 1 }))
    .map(interval => interval.start)
    .filter((dt): dt is DateTime => dt !== null)
)

const submittingCreateGroup = ref(false)

function submitCreateGroup(event: SubmitEvent) {
  if (!event.target)
    return Promise.resolve()
  const form = new FormData(event.target as HTMLFormElement)
  const group = {
    name: (form.get('group-name') ?? '') as string,
    groupSource: GroupSource.Local,
    groupType: GroupType.Adhoc,
  }

  submittingCreateGroup.value = true

  if (user.value && !allSelectedUserIds.value.includes(user.value?.userId)) {
    selectedUserIds.value.push(user.value.userId)
  }

  return new Promise((resolve, reject) => {
    createGroup(
      group as Group,
      allSelectedUsers
        .value
        .map(user => ({
          ...user,
          role: user.primaryRole,
          groupRole: user.userId === userId.value ? GroupRole.Admin : GroupRole.Member,
        }))
    )
      .then(([group, users]) => {
        AuToast.success(t('groups.createSuccess', { name: group.name }))
        selectedGroupIds.value = [group.groupId]
        selectedUserIds.value = []
        resolve([group, users])
      })
      .catch((error) => {
        AuToast.error(t('groups.saveError'))
        reject(error)
      })
      .finally(() => submittingCreateGroup.value = false)
  })
}

const [DefineDateSelect, ReuseDateSelect] = createReusableTemplate<{ options: DateTime[] }, { default: { option: DateTime } }>()

function loadGroupsByIds(ids: string[]) {
  mapFilter(
    ids,
    groupId => sortedGroups.value.find(group => group.groupId === groupId),
    notNullOrUndefined,
  ).forEach(group => loadGroupMembers(group))
}
</script>

<template>
  <!-- eslint-disable vue/no-multiple-template-root -->
  <DefineDateSelect v-slot="{ options, $slots }">
    <Select
      :model-value="selectedDateTime.toISO()"
      variant="outline"
      disable-portal
      :options
      :option-id="(date) => date.toISO() ?? ''"
      @update:model-value="(value) => selectedDateTime = value ? DateTime.fromISO(value) : initialDateTime"
    >
      <template #label="{ option }">
        <component :is="$slots.default" :option />
      </template>
    </Select>
  </DefineDateSelect>

  <Button
    v-if="showShareButton && !isEditMode"
    v-bind="$attrs"
    @click.stop.prevent="dialogOpen = true"
  >
    <font-awesome-icon v-if="!isEditMode" :icon="faArrowTurnRight" />
    {{ props.showLabel ? (isEditMode ? t('share.messages.edit') : t('share.messages.share')) : '' }}
  </Button>
  <button
    v-if="showShareButton && isEditMode"
    class="cursor-pointer underline hover:text-gray-50 focus-visible:ring"
    @click.stop.prevent="dialogOpen = true"
  >
    {{ props.showLabel ? (isEditMode ? t('share.edit') : t('share.messages.share')) : '' }}
  </button>
  <AuDialog v-model:open="dialogOpen" :title="dialogTitle">
    <template #content>
      <h2 class="text-green-40 mb-8 text-xl font-bold">
        {{ props.resource?.title || props.list?.name || props.task?.name || '' }}
      </h2>

      <div v-show="step === 0" class="space-y-8">
        <div>
          <h3 class="text-gray-40 mb-3.5 text-base font-medium uppercase">
            {{ t('groups.groups') }}
          </h3>

          <CheckboxGroupRoot v-model="selectedGroupIds">
            <AccordionRoot type="multiple" @update:model-value="$event && loadGroupsByIds(Array.isArray($event) ? $event : [$event])">
              <AccordionItem
                v-for="group in sortedGroups"
                :key="group.groupId"
                v-slot="{ open }"
                :value="group.groupId"
              >
                <AccordionHeader class="grid grid-cols-[minmax(0,1fr)_max-content]">
                  <Button
                    class="flex items-center gap-2"
                    type="label"
                    variant="ghost"
                  >
                    <AuCheckbox :value="group.groupId" />
                    {{ group.name }}
                  </Button>
                  <AccordionTrigger as-child>
                    <Button shape="square" variant="ghost">
                      <font-awesome-icon
                        :icon="open ? faChevronUp : faChevronDown"
                      />
                    </Button>
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent class="prose">
                  <ul>
                    <li v-for="user in groupMap.get(group.groupId)">
                      {{ user.fullName }}
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </AccordionRoot>
          </CheckboxGroupRoot>
        </div>

        <AuCallout
          v-if="isTeacherItem"
          variant="warning"
          :title="t('share.teacher.title')"
        >
          {{ t('share.teacher.message') }}
        </AuCallout>

        <label class="block">
          <h3 class="text-gray-40 mb-3.5 text-base font-medium uppercase">
            {{ t('assign.searchForStudents') }}
          </h3>

          <SearchMembers
            :model-value="selectedUsers.map((user) => ({ ...user, role: user.primaryRole, groupRole: GroupRole.Member }))"
            students-only
            @update:model-value="(groupUsers) => selectedUsers = groupUsers?.map(user => mapGroupMemberToTaskUser(user)) ?? []"
          />
        </label>

        <div v-if="allSelectedUserIds.length" class="flex flex-wrap gap-2">
          <Button
            v-for="group in selectedGroups"
            :key="group.groupId"
            variant="outline"
            size="sm"
            @click="unselectGroup(group.groupId)"
          >
            {{ group.name }}
            <font-awesome-icon :icon="faXmark" />
          </Button>
          <Button
            v-for="user in selectedUsers"
            :key="user.userId"
            variant="outline"
            size="sm"
            @click="unselectUser(user.userId)"
          >
            {{ user.firstName }}
            <span
              v-if="user.primaryRole === UserRole.Teacher"
              class="text-coral-60 rounded-sm bg-yellow-50 px-2 py-0.5 text-xs font-bold tracking-widest uppercase"
            >L
            </span>
            <font-awesome-icon :icon="faXmark" />
          </Button>
        </div>

        <AuDialog :title="t('groups.newGroup')">
          <template #trigger>
            <Button :disabled="allSelectedUserIds.length < 1">
              <font-awesome-icon :icon="faPlus" />
              {{ t('groups.newGroup') }}
            </Button>
          </template>

          <template #content="{ close }">
            <form class="space-y-8" @submit.prevent="submitCreateGroup($event).then(close)">
              <label class="block">
                <h3 class="text-gray-40 mb-3.5 text-base font-medium uppercase">
                  {{ t('groups.groupName') }}
                </h3>
                <input
                  type="text"
                  name="group-name"
                  class="bg-gray-10 text-gray-60 block w-full rounded-lg p-2.5 text-base focus:ring"
                  :placeholder="t('groups.groupNamePlaceholder')"
                >
              </label>

              <p>{{ t('groups.membersCount', { count: allSelectedUserIds.length }) }}</p>

              <div class="flex items-center gap-4">
                <Button type="submit" :disabled="submittingCreateGroup">
                  {{ t('groups.saveGroup') }}
                </Button>

                <AuSpinner
                  v-if="submittingCreateGroup"
                  size="sm"
                  color="var(--color-seagreen-30)"
                />
              </div>
            </form>
          </template>
        </AuDialog>

        <div class="grid auto-cols-fr grid-flow-col items-center gap-4">
          <Button
            center
            variant="outline"
            size="lg"
            @click="dialogOpen = false"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            center
            size="lg"
            :disabled="allSelectedUserIds.length < 1"
            @click="step = 1"
          >
            {{ t('share.messages.share') }}...
          </Button>
        </div>
      </div>

      <div v-show="step === 1" class="space-y-8">
        <RadioGroupRoot
          v-if="!isEditMode"
          v-model="shareWhen"
          class="space-y-4"
        >
          <label class="flex items-center gap-2">
            <AuRadio
              value="now"
            />
            {{ t('share.options.now') }}
          </label>

          <div class="flex items-center gap-2">
            <label class="contents">
              <AuRadio
                value="later"
              />
              {{ t('share.options.time') }}
            </label>
          </div>
          <div
            v-show="shareWhen === 'later'"
            class="flex items-center gap-2"
          >
            <ReuseDateSelect v-slot="{ option }" :options="weeks">
              {{ t('shared.week', { weekNumber: option.weekNumber }) }}
            </ReuseDateSelect>

            <ReuseDateSelect v-slot="{ option }" :options="days">
              {{ option.weekdayShort }}
            </ReuseDateSelect>

            <ReuseDateSelect v-slot="{ option }" :options="times">
              {{ option.toFormat('HH:mm') }}
            </ReuseDateSelect>
          </div>
        </RadioGroupRoot>

        <div v-else class="mr-auto space-y-2 font-medium">
          <h3
            class="text-gray-40 text-base font-medium uppercase"
            v-text="t('share.options.time')"
          />
          <div class="flex items-center gap-2">
            <ReuseDateSelect v-slot="{ option }" :options="weeks">
              {{ t('shared.week', { weekNumber: option.weekNumber }) }}
            </ReuseDateSelect>

            <ReuseDateSelect v-slot="{ option }" :options="days">
              {{ option.weekdayShort }}
            </ReuseDateSelect>

            <ReuseDateSelect v-slot="{ option }" :options="times">
              {{ option.toFormat('HH:mm') }}
            </ReuseDateSelect>
          </div>
        </div>

        <label class="flex items-center gap-2">
          <AuCheckbox
            v-model="homework"
            type="checkbox"
          />
          {{ t('share.messages.homework') }}
        </label>

        <label v-if="props.list" class="flex flex-col gap-2">
          <h3
            class="text-gray-40 text-base font-medium uppercase"
            v-text="t('share.messages.optionalMessage')"
          />
          <textarea
            v-model="textareaValue"
            rows="4"
            class="bg-gray-10 text-gray-60 block w-full rounded-lg p-2.5 text-base focus:ring"
            maxlength="200"
            :placeholder="t('share.placeholders.messageField')"
          />
        </label>

        <AuCallout v-if="!isInFuture" variant="warning">
          {{ t('share.messages.future') }}
        </AuCallout>

        <div class="grid auto-cols-fr grid-flow-col items-center gap-4">
          <Button
            center
            variant="outline"
            size="lg"
            @click="step = 0"
          >
            {{ t('previous') }}
          </Button>
          <Button
            center
            :disabled="!canSubmit || isSubmitting"
            size="lg"
            :data-pendo="isEditMode ? PendoFeature.Share.Edit : PendoFeature.Share.Simple"
            @click="submit"
          >
            {{ props.list ? t('share.messages.shareList') : t('share.messages.shareResource') }}
          </Button>
        </div>
      </div>
    </template>
  </AuDialog>
</template>
